import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, Search, Leaf, Star } from "lucide-react";
import { API, absUrl } from "../../lib/api";
import { useAuthed } from "../../lib/adminApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import DishDialog from "./DishDialog";
import { toast } from "sonner";

function AdminDishCard({ it, onEdit, onSoldOut, onActive, onDelete }) {
  const active = it.active !== false;
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 overflow-hidden ${active ? "" : "opacity-60"}`} data-testid={`admin-menu-${it.id}`}>
      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
        {it.image_url && <img src={absUrl(it.image_url)} className="h-full w-full object-cover" alt={it.name} loading="lazy" />}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          {!active && <span className="bg-red-600 text-white text-[10px] px-2 py-0.5 rounded-full">Hidden from website</span>}
          {it.sold_out && <span className="bg-gray-800 text-white text-[10px] px-2 py-0.5 rounded-full">Not available</span>}
          {it.image_is_sample && <span className="bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full">Sample image</span>}
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="font-semibold text-sm line-clamp-2">{it.name}</div>
          <div className="font-bold text-[#CC1A00] text-sm whitespace-nowrap">KSh {it.price}</div>
        </div>
        <div className="flex flex-wrap gap-1 mt-1.5">
          <Badge variant="outline" className="text-[10px]">{it.category}</Badge>
          {it.vegetarian && <Badge variant="outline" className="text-[10px] text-green-700"><Leaf size={10} className="mr-0.5" />Veg</Badge>}
          {it.featured && <Badge variant="outline" className="text-[10px]">Featured</Badge>}
          {it.special && <Badge variant="outline" className="text-[10px] text-[#CC1A00]"><Star size={10} className="mr-0.5" />Special</Badge>}
        </div>
        <div className="flex items-center justify-between mt-3 gap-2">
          <div className="flex flex-col gap-1.5">
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <Switch checked={active} onCheckedChange={() => onActive(it)} data-testid={`active-dish-${it.id}`} /> Show on website
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <Switch checked={!!it.sold_out} onCheckedChange={() => onSoldOut(it)} data-testid={`soldout-dish-${it.id}`} /> Not available
            </label>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(it)} data-testid={`edit-dish-${it.id}`}><Pencil size={12} /></Button>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => onDelete(it)} data-testid={`delete-dish-${it.id}`}><Trash2 size={12} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MenuManager() {
  const call = useAuthed();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [extras, setExtras] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");
  const reqId = useRef(0);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    try {
      const [m, c, e] = await Promise.all([call("get", "/admin/menu"), axios.get(`${API}/categories`), call("get", "/admin/extras")]);
      if (id !== reqId.current) return;
      setItems(m.data); setCategories(c.data); setExtras(e.data);
    } catch { toast.error("Could not load menu"); }
  }, [call]);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => items.filter((i) =>
    (cat === "All" || i.category === cat) && i.name.toLowerCase().includes(query.trim().toLowerCase())), [items, cat, query]);

  const soldOut = async (it) => {
    try {
      await call("patch", `/admin/menu/${it.id}`, { sold_out: !it.sold_out });
      toast.success(`${it.name} ${it.sold_out ? "available again" : "marked not available"}`);
      load();
    } catch { toast.error("Update failed"); }
  };
  const setActive = async (it) => {
    const next = it.active === false;
    try {
      await call("patch", `/admin/menu/${it.id}`, { active: next });
      toast.success(`${it.name} ${next ? "shown on" : "hidden from"} the website`);
      load();
    } catch { toast.error("Update failed"); }
  };
  const del = async (it) => {
    if (!window.confirm(`Delete ${it.name}?`)) return;
    try { await call("delete", `/admin/menu/${it.id}`); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="font-display font-extrabold text-xl sm:text-2xl mr-auto">Menu Items <span className="text-gray-400 text-base">({items.length})</span></h1>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="admin-menu-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search dishes" className="pl-8 w-48" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-52" data-testid="admin-menu-category"><SelectValue /></SelectTrigger>
          <SelectContent>{["All", ...categories].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="mlobox-gradient text-white rounded-full" data-testid="add-menu-btn"><Plus size={14} className="mr-1" /> Add dish</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {shown.map((it) => <AdminDishCard key={it.id} it={it} onEdit={(x) => { setEditing(x); setOpen(true); }} onSoldOut={soldOut} onActive={setActive} onDelete={del} />)}
      </div>
      <DishDialog open={open} onOpenChange={setOpen} item={editing} categories={categories} extras={extras} onSaved={load} />
    </div>
  );
}
